import { forwardRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import { ComponentType } from '@vgtv/api-client/lib/component';

import {
  getAssetLabels,
  getAssetMainImage,
} from '../../../shared/helpers/assetHelpers';
import { getImageSources } from '../../../helpers/images';
import { AssetVertical } from '../../Asset/AssetVertical/AssetVertical';
import { LiveAssetBadge } from '../../Asset/shared/LiveAssetBadge/LiveAssetBadge';

import * as styles from './LiveAssetsGroup.module.scss';

export interface LiveAssetsGroupProps {
  title?: string;
  categoryId?: SvpCategoryId;
  assets?: SvpAssetAdditional[];
}

const LiveAssetsGroup = forwardRef<HTMLDivElement, LiveAssetsGroupProps>(
  ({ title, categoryId, assets = [] }, ref) => {
    const { t } = useTranslation();

    const syntheticComponent = useMemo(
      () =>
        ({
          type: ComponentType.FILTERED_LIST,
          categoryId: categoryId!,
        }) as const,
      [categoryId]
    );

    return (
      <div className={styles.group} ref={ref}>
        {title && (
          <header>
            <h2 className={styles.title}>{title}</h2>
          </header>
        )}

        {assets.length < 1 && (
          <p className={styles.text}>{t('No video for this date')}</p>
        )}

        {assets.length >= 1 && (
          <div className={classNames(styles.grid)}>
            {assets.map((asset) => {
              const { id, description, title } = asset;

              return (
                <AssetVertical
                  id={id}
                  key={id}
                  badge={<LiveAssetBadge asset={asset} />}
                  title={title}
                  description={description}
                  labels={getAssetLabels(asset, syntheticComponent)}
                  imageSources={getImageSources({
                    imageUrl: getAssetMainImage(asset),
                    imagePreset: 'assetsSlider',
                    aspectRatio: 'horizontal',
                  })}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

export { LiveAssetsGroup };
